<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Add New Expense
        </v-card-title>
        <v-card-text>
          <!-- Classification -->
          <h4 class="text-subtitle-1 font-weight-bold">Classification</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="6">
              <v-select
                v-model="expenseType"
                :items="typeOptions"
                outlined
                prepend-inner-icon="mdi-shape-outline"
                hint="Direct = tied to goods movement (freight, packing). Indirect = running overhead (salary, light bill)."
                persistent-hint
                @change="form.expense_category_id = null"
              >
                <template v-slot:label>
                  Expense Type
                  <span style="color: #f44336; font-weight: bold">*</span>
                </template>
              </v-select>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.expense_category_id"
                :items="filteredCategories"
                item-text="name"
                item-value="id"
                outlined
                :disabled="expenseType === null"
                :hint="
                  expenseType === null
                    ? 'Pick an expense type first'
                    : 'Categories are managed under Expense Categories'
                "
                persistent-hint
                prepend-inner-icon="mdi-tag-outline"
                :error-messages="errors.expense_category_id"
              >
                <template v-slot:label>
                  Category
                  <span style="color: #f44336; font-weight: bold">*</span>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Details -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Details</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="12">
              <v-text-field
                v-model="form.title"
                outlined
                placeholder="e.g. July Salary - Ramesh Kumar"
                :error-messages="errors.title"
              >
                <template v-slot:label>
                  Title / Description
                  <span style="color: #f44336; font-weight: bold">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.amount"
                outlined
                type="number"
                min="0"
                step="0.01"
                prefix="₹"
                placeholder="e.g. 25000"
                :error-messages="errors.amount"
              >
                <template v-slot:label>
                  Amount
                  <span style="color: #f44336; font-weight: bold">*</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.expense_date"
                outlined
                type="date"
                :error-messages="errors.expense_date"
              >
                <template v-slot:label>
                  Expense Date
                  <span style="color: #f44336; font-weight: bold">*</span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Payment -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Payment</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="6">
              <v-select
                v-model="form.payment_mode"
                :items="paymentModes"
                outlined
                prepend-inner-icon="mdi-cash"
                :error-messages="errors.payment_mode"
              >
                <template v-slot:label>
                  Payment Mode
                  <span style="color: #f44336; font-weight: bold">*</span>
                </template>
              </v-select>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.reference_number"
                outlined
                label="Reference Number"
                placeholder="Cheque no. / UTR / txn id"
                :error-messages="errors.reference_number"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Attachment -->
          <h4 class="text-subtitle-1 font-weight-bold mt-6">Bill / Receipt</h4>
          <v-divider class="mb-4 mt-1"></v-divider>
          <v-row>
            <v-col sm="12">
              <v-file-input
                v-model="attachment"
                outlined
                label="Attach Bill / Receipt"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                hint="JPG, PNG or PDF. Max 5 MB."
                persistent-hint
                show-size
                clearable
                :error-messages="errors.attachment"
              ></v-file-input>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"></v-divider>
          <div class="text-right">
            <v-btn text class="mr-2" @click="$router.back()">Cancel</v-btn>
            <v-btn
              :loading="isLoading"
              :disabled="isLoading"
              rounded
              color="primary"
              dark
              @click="save"
            >
              Create
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Error -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: red; font-size: 90px">
                mdi-alert-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: red">Error!</h1>
                <br />
                <h3>{{ errorMessage }}</h3>
                <br />
                <v-btn color="error" @click="errorDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "ExpenseCreatePage",
  data() {
    return {
      form: {
        expense_category_id: null,
        title: "",
        amount: null,
        expense_date: new Date().toISOString().slice(0, 10),
        payment_mode: 0,
        reference_number: null,
        remarks: null,
      },
      expenseType: null,
      attachment: null,
      categories: [],
      isLoading: false,
      errorDialog: false,
      errorMessage: "",
      typeOptions: [
        { text: "Direct", value: 0 },
        { text: "Indirect", value: 1 },
      ],
      paymentModes: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    filteredCategories() {
      if (this.expenseType === null) return [];
      return this.categories.filter((c) => c.type === this.expenseType);
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "expense" },
        });
        this.categories = response.data.expense_categories || [];
      } catch (error) {
        console.log(error);
      }
    },

    buildPayload() {
      const data = new FormData();
      Object.keys(this.form).forEach((key) => {
        const value = this.form[key];
        if (value !== null && value !== undefined && value !== "") {
          data.append(key, value);
        }
      });
      if (this.attachment) {
        data.append("attachment", this.attachment);
      }
      return data;
    },

    async save() {
      this.isLoading = true;
      this.$store.dispatch("errors/setErrors", {});
      try {
        await axios.post("/expenses", this.buildPayload(), {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.$router.push("/expenses");
      } catch (error) {
        console.log(error);
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            this.$store.dispatch("errors/setErrors", error.response.data.errors);
          } else {
            this.errorMessage =
              error.response.data.message || "Failed to create expense";
            this.errorDialog = true;
          }
        }
      }
      this.isLoading = false;
    },
  },
};
</script>
